import React, { useState } from 'react';
import { AxiosResponse } from 'axios';

import useStores from '@stores/useStores';
import { apiAuthCaller } from '@services/apiCaller';
import { CatchErr } from '@utils/types';
import { getApiErrorMessage } from '@components/common/util/util';
import { IDestDefinition } from '@stores/destinationDefsList';

import { OAuthModalPopup } from './oAuthModal';

interface DestinationAuthSectionProps {
  destinationDef: IDestDefinition;
  initiallySelectedAccId: string;
  updateAccount: (id: string) => void;
  isDisabled: boolean;
}

type DestinationAccount = {
  id: string;
  name: string;
  createdAt: string;
  metadata: { email: string };
};

export const DestinationAuthSection = ({
  destinationDef,
  initiallySelectedAccId,
  updateAccount,
  isDisabled = false,
}: DestinationAuthSectionProps) => {
  const { messagesStore } = useStores();
  const role = destinationDef?.config?.auth.role || destinationDef!.name;
  const [savedAccountId, setSavedAccountId] = useState(initiallySelectedAccId);

  const getAccounts = async (): Promise<DestinationAccount[]> => {
    const response: AxiosResponse<DestinationAccount[]> = await apiAuthCaller().get(
      `/destination/accounts/${
        destinationDef.config?.auth?.role || destinationDef.name.toLowerCase()
      }`,
    );
    return response.data;
  };

  const deleteAccount = React.useCallback(
    async (id: string, onSuccess: () => void) => {
      try {
        await apiAuthCaller().delete(`/destination/accounts/${id}`);
        onSuccess();
      } catch (err: CatchErr) {
        messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to delete account'));
      }
    },
    [role],
  );

  const onSave = (rudderAccountId: string) => {
    setSavedAccountId(rudderAccountId);
    updateAccount(rudderAccountId);
  };

  return (
    <OAuthModalPopup
      lastSavedAccountId={savedAccountId}
      destinationDef={destinationDef}
      getAccounts={getAccounts}
      deleteAccount={deleteAccount}
      onSaveCb={onSave}
      isDisabled={isDisabled}
    />
  );
};
